import {
  ActionIcon,
  Button,
  Input,
  MantineColorsTuple,
  MultiSelect,
  Pagination,
  Badge,
  Radio,
  Switch,
  Tabs,
  createTheme,
} from '@mantine/core';
import { variantColorResolver } from '@utils/statusColors';
import inputClasses from './ComponentDefaultCss/input.module.scss';
import buttonClasses from './ComponentDefaultCss/button.module.scss';
import switchClasses from './ComponentDefaultCss/switch.module.scss';
import paginationClasses from './ComponentDefaultCss/pagination.module.scss';
import multiSelectClasses from './ComponentDefaultCss/multiSelect.module.scss';
import datePickerClasses from './ComponentDefaultCss/datePicker.module.scss';
import radioClasses from './ComponentDefaultCss/radio.module.scss';

const overlayProps = {
  backgroundOpacity: 0.4,
};

// Brand yellow palette — shade 5 = var(--yellow-light) #f4d751, shade 6 = var(--yellow) #f2d032.
// Mantine uses shade 6 for selected state and shade 1 for in-range / light backgrounds.
const brandYellow: MantineColorsTuple = [
  '#fff9e6',
  '#fff2c4',
  '#fde8a0',
  '#fadf7d',
  '#f6d558',
  '#f4d751',
  '#f2d032',
  '#d1b223',
  '#a68b19',
  '#7d6810',
];

export const theme = createTheme({
  fontFamily: "'Mulish Variable', sans-serif",
  autoContrast: true,
  luminanceThreshold: 0.4,
  variantColorResolver,
  colors: {
    brandYellow,
  },
  components: {
    ActionIcon: ActionIcon.extend({
      styles: (_theme, props) => ({
        root:
          props.variant === 'transparent' && props.disabled
            ? { backgroundColor: 'transparent' }
            : {},
      }),
    }),
    Button: Button.extend({
      classNames: buttonClasses,
      vars: (_theme, props) => {
        if (props.variant === 'brand') {
          return {
            root: {
              '--button-bg': 'var(--yellow)',
              '--button-hover': 'var(--yellow-light)',
              '--button-color': 'var(--black)',
              '--button-hover-color': 'var(--black)',
            },
          };
        }

        if (props.variant === 'negative-brand') {
          return {
            root: {
              '--button-bg': 'var(--black)',
              '--button-hover': 'var(--dark-gray)',
              '--button-color': 'var(--yellow)',
              '--button-hover-color': 'var(--yellow)',
            },
          };
        }

        return { root: {} };
      },
    }),
    Input: Input.extend({
      classNames: inputClasses,
    }),
    SegmentedControl: {
      defaultProps: {
        color: 'var(--yellow)',
      },
      styles: {
        root: {
          backgroundColor: 'var(--white)',
          border: '1px solid var(--border-color)',
          borderRadius: '4px',
          overflow: 'hidden',
          padding: '0px',
        },
        indicator: {
          borderRadius: 0,
        },
        label: {
          color: 'var(--black)',
        },
      },
    },
    Slider: {
      styles: {
        bar: {
          backgroundColor: 'var(--yellow)',
        },
        thumb: {
          borderColor: 'var(--yellow)',
          backgroundColor: 'var(--yellow)',
        },
        mark: {
          border: '2px solid var(--black)',
          backgroundColor: 'var(--black)',
        },
      },
    },
    Switch: Switch.extend({
      classNames: switchClasses,
    }),
    Tabs: Tabs.extend({
      defaultProps: {
        color: 'var(--yellow)',
      },
      styles: (_theme, props) => ({
        root: {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        },
        list: {
          justifyContent: 'flex-start',
          width: 'auto',
        },
        tab: {
          fontSize: '1rem',
          // Pills sub-tabs get a boxed outline; the active one is covered by its yellow fill.
          ...(props.variant === 'pills' && {
            border: '1.6px solid var(--light-silver, #E1E6EB)',
            borderRadius: '4px',
          }),
        },
        panel: {
          display: 'flex',
          gap: '1rem',
          flex: 1,
          overflow: 'auto',
          padding: '4px',
        },
      }),
    }),
    Pagination: Pagination.extend({
      classNames: paginationClasses,
      defaultProps: {
        hideWithOnePage: true,
        withEdges: true,
        withControls: true,
      },
    }),
    Checkbox: {
      defaultProps: {
        color: 'var(--yellow)',
        iconColor: 'var(--black)',
      },
    },
    DatePickerInput: {
      classNames: datePickerClasses,
    },
    DatePicker: {
      defaultProps: {
        color: 'brandYellow',
      },
    },
    Radio: Radio.extend({
      classNames: radioClasses,
      defaultProps: {
        iconColor: 'var(--black)',
        color: 'var(--yellow)',
      },
    }),
    MultiSelect: MultiSelect.extend({
      classNames: multiSelectClasses,
      defaultProps: {
        color: 'var(--yellow)',
        chevronColor: 'transparent',
      },
      styles: {
        input: {
          padding: '0.5rem',
          caretColor: 'var(--black)',
        },
      },
    }),
    Skeleton: {
      defaultProps: {
        radius: 4,
      },
    },
    Paper: {
      defaultProps: {
        withBorder: true,
      },
      styles: {
        root: {
          border: '1px solid var(--anti-flash-white)',
          borderRadius: '4px',
          boxShadow: '0 0 16px rgba(208, 212, 225, 0.65)', // update --box-shadow var with this value from figma new designs
          margin: 0,
        },
      },
    },
    Notifications: {
      defaultProps: {
        zIndex: 2000, // above modals
      },
    },
    Modal: {
      defaultProps: {
        overlayProps: {
          ...overlayProps,
        },
      },
      styles: {
        content: {
          border: 'none',
          boxShadow: 'none',
        },
      },
    },
    Drawer: {
      defaultProps: {
        overlayProps: {
          ...overlayProps,
        },
      },
      styles: {
        content: {
          border: 'none',
          boxShadow: 'none',
        },
      },
    },
    Badge: Badge.extend({
      defaultProps: {
        radius: 'xs',
      },
      styles: {
        root: {
          fontWeight: 700,
          fontSize: '13px',
        },
      },
    }),
  },
});
